import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Box,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  CircularProgress
} from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

interface PaymentTransaction {
  _id: string;
  amount: number;
  currency: string;
  status: 'pending' | 'processing' | 'succeeded' | 'failed' | 'refunded' | 'disputed';
  type: 'single_session' | 'block_booking' | 'multi_session' | 'manual';
  stripePaymentIntentId?: string;
  session?: {
    _id: string;
    date: string;
    duration: number;
    status: string;
  };
  sessions?: string[];
  blockBooking?: {
    _id: string;
    numberOfSessions: number;
    sessionsUsed: number;
  };
  metadata?: {
    method?: string;
    reference?: string;
    assessmentFeePence?: number;
    sessionsAllocatedPence?: number;
    balanceAppliedPence?: number;
    allocatedPence?: number;
    creditPence?: number;
    repairNote?: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface SessionRecord {
  _id: string;
  date: string;
  duration: number;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
  paymentStatus: 'pending' | 'processing' | 'paid' | 'failed';
  fee: number;
  counsellor: {
    firstName: string;
    lastName: string;
  };
  notes?: string;
  createdAt: string;
}

interface BlockBooking {
  _id: string;
  numberOfSessions: number;
  fee: number;
  startDate: string;
  endDate?: string;
  status: string;
  sessionsUsed: number;
  isPaid: boolean;
  sessions: string[];
  createdAt: string;
}

interface PaymentSummary {
  totalPaid: number;
  totalOutstanding: number;
  assessmentFee?: number;
  assessmentFeePaid?: boolean;
  assessmentFeeWaived?: boolean;
  paymentType: 'per_session' | 'block_booking' | 'employer_managed';
  employerReference?: string;
  activeBlockBooking?: BlockBooking;
  sessionRate?: number;
}

interface PaymentSessionHistoryProps {
  clientId: string;
}

interface OutstandingSession {
  sessionId: string;
  date: string;
  fee: number;
  paymentStatus: string;
  pendingRequestId?: string;
  amountDue?: number;
}

interface ActivePaymentRequest {
  _id: string;
  amount: number;
  createdAt: string;
  expiresAt: string;
  sessionIds: string[];
  checkoutUrl?: string;
}

const PaymentSessionHistory: React.FC<PaymentSessionHistoryProps> = ({ clientId }) => {
  const { user } = useAuth();
  const isCounsellor = user?.role === 'counsellor';
  const isAdmin = user?.role === 'administrator';
  const [activeTab, setActiveTab] = useState(0);
  const [payments, setPayments] = useState<PaymentTransaction[]>([]);
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [blockBookings, setBlockBookings] = useState<BlockBooking[]>([]);
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary | null>(null);
  const [client, setClient] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createPaymentOpen, setCreatePaymentOpen] = useState(false);
  const [newPayment, setNewPayment] = useState({
    type: 'single_session',
    amount: '',
    sessionId: '',
    method: 'bank_transfer',
    reference: ''
  });
  const [paymentRequestSuccess, setPaymentRequestSuccess] = useState<string | null>(null);
  const [paymentRecordSuccess, setPaymentRecordSuccess] = useState<string | null>(null);
  const [receiptPayment, setReceiptPayment] = useState<PaymentTransaction | null>(null);
  const [receiptEmailState, setReceiptEmailState] = useState<{ sending: boolean; sent: string | null; error: string | null }>({ sending: false, sent: null, error: null });
  const [writeOffOpen, setWriteOffOpen] = useState(false);
  const [writeOffReason, setWriteOffReason] = useState('');

  const handleToggleFeeWaiver = async () => {
    try {
      await api.patch(`/api/clients/${clientId}/assessment-fee-waived`, { waived: !paymentSummary?.assessmentFeeWaived });
      await fetchPaymentSessionData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update fee waiver');
    }
  };

  const handleWriteOff = async () => {
    try {
      const res = await api.post(`/api/clients/${clientId}/write-off-debt`, { reason: writeOffReason || undefined });
      setWriteOffOpen(false);
      setWriteOffReason('');
      setPaymentRecordSuccess(
        `Debt written off${res.data.writtenOffPence ? ` (£${(res.data.writtenOffPence / 100).toFixed(2)})` : ''}` +
        (res.data.sessionsWaived ? ` - ${res.data.sessionsWaived} session${res.data.sessionsWaived === 1 ? '' : 's'} waived` : '')
      );
      await fetchPaymentSessionData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to write off debt');
    }
  };

  const handleEmailReceipt = async () => {
    if (!receiptPayment) return;
    setReceiptEmailState({ sending: true, sent: null, error: null });
    try {
      const res = await api.post(`/api/clients/${clientId}/payments/${receiptPayment._id}/send-receipt`);
      setReceiptEmailState({ sending: false, sent: res.data.message || 'Receipt emailed', error: null });
    } catch (err: any) {
      setReceiptEmailState({ sending: false, sent: null, error: err.response?.data?.message || 'Failed to email receipt' });
    }
  };

  // Outstanding payment request modal
  const [outstandingSessions, setOutstandingSessions] = useState<OutstandingSession[]>([]);
  const [activePaymentRequest, setActivePaymentRequest] = useState<ActivePaymentRequest | null>(null);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [selectedSessionIds, setSelectedSessionIds] = useState<string[]>([]);
  const [loadingOutstanding, setLoadingOutstanding] = useState(false);
  const [sendingOutstandingRequest, setSendingOutstandingRequest] = useState(false);
  const [resendingRequest, setResendingRequest] = useState(false);
  const [outstandingError, setOutstandingError] = useState<string | null>(null);

  const formatSafeDate = (dateString: string | Date | null | undefined, formatStr: string = 'dd/MM/yyyy HH:mm') => {
    if (!dateString) return 'N/A';
    
    try {
      let date: Date;
      if (typeof dateString === 'string') {
        // Try parsing as ISO string first, then as regular Date
        date = dateString.includes('T') ? parseISO(dateString) : new Date(dateString);
      } else {
        date = new Date(dateString);
      }
      
      if (!isValid(date)) {
        return 'Invalid Date';
      }
      
      return format(date, formatStr);
    } catch (error) {
      console.error('Date formatting error:', error, 'for date:', dateString);
      return 'Invalid Date';
    }
  };

  const formatPounds = (amount: number) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  const formatCurrency = (amount: number) => {
    // If amount is less than 100, it's likely already in pounds (e.g., 30)
    // If amount is >= 100, it's in pence (e.g., 3000 = £30)
    const amountInPounds = amount >= 100 ? amount / 100 : amount;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amountInPounds);
  };

  const formatPaymentType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const fetchPaymentSessionData = useCallback(async () => {
    try {
      setLoading(true);
      const [paymentsRes, sessionsRes, blockBookingsRes, summaryRes, clientRes] = await Promise.all([
        api.get(`/api/clients/${clientId}/payments`),
        api.get(`/api/clients/${clientId}/sessions`),
        api.get(`/api/clients/${clientId}/block-bookings`),
        api.get(`/api/clients/${clientId}/payment-summary`),
        api.get(`/api/clients/${clientId}`)
      ]);

      setPayments(paymentsRes.data);
      setSessions(sessionsRes.data);
      setBlockBookings(blockBookingsRes.data);
      setPaymentSummary(summaryRes.data);
      setClient(clientRes.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch payment and session data');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchPaymentSessionData();
  }, [fetchPaymentSessionData]);

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'succeeded':
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'processing':
        return 'info';
      case 'failed':
      case 'disputed':
        return 'error';
      case 'refunded':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getSessionStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'scheduled':
        return 'info';
      case 'cancelled':
        return 'warning';
      case 'no_show':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleCreatePayment = async () => {
    try {
      const response = await api.post(`/api/clients/${clientId}/payments`, newPayment);
      setCreatePaymentOpen(false);
      if (newPayment.type === 'manual') {
        const parts: string[] = [];
        const feePence = response.data.assessmentFeePence || 0;
        if (feePence > 0) parts.push(`£${(feePence / 100).toFixed(2)} assessment fee`);
        const settled = response.data.settledSessionIds?.length || 0;
        if (settled > 0) parts.push(`${settled} session${settled === 1 ? '' : 's'}`);
        const balancePence = response.data.balanceAppliedPence || 0;
        if (balancePence > 0) parts.push(`£${(balancePence / 100).toFixed(2)} account balance`);
        const creditPence = response.data.creditPence || 0;
        setPaymentRecordSuccess(
          `Payment recorded: ${parts.length ? parts.join(' + ') + ' settled' : 'no outstanding debt to settle'}` +
          (creditPence > 0 ? `; £${(creditPence / 100).toFixed(2)} held as account credit` : '')
        );
      } else {
        setPaymentRecordSuccess('Payment recorded');
      }
      setNewPayment({ amount: '', type: 'single_session', sessionId: '', method: 'bank_transfer', reference: '' });
      // Small delay to ensure database updates are complete
      await new Promise(resolve => setTimeout(resolve, 500));
      // Refresh all data to update the lists
      await fetchPaymentSessionData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create payment');
    }
  };

  const fetchOutstandingSessions = useCallback(async () => {
    try {
      setLoadingOutstanding(true);
      setOutstandingError(null);
      const response = await api.get(`/api/payments/outstanding-sessions/${clientId}`);
      setOutstandingSessions(response.data.sessions || []);
      setActivePaymentRequest(response.data.activeRequest || null);
      // Pre-select sessions that aren't already covered by an active request
      setSelectedSessionIds(
        (response.data.sessions || [])
          .filter((s: OutstandingSession) => !s.pendingRequestId)
          .map((s: OutstandingSession) => s.sessionId)
      );
    } catch (err: any) {
      setOutstandingError(err.response?.data?.error || 'Failed to fetch outstanding sessions');
    } finally {
      setLoadingOutstanding(false);
    }
  }, [clientId]);

  const handleOpenRequestModal = () => {
    setRequestModalOpen(true);
    fetchOutstandingSessions();
  };

  const toggleSessionSelected = (sessionId: string) => {
    setSelectedSessionIds((prev) =>
      prev.includes(sessionId) ? prev.filter((id) => id !== sessionId) : [...prev, sessionId]
    );
  };

  const selectableSessions = outstandingSessions.filter((s) => !s.pendingRequestId);
  const allSelectableSelected =
    selectableSessions.length > 0 && selectableSessions.every((s) => selectedSessionIds.includes(s.sessionId));

  const handleToggleSelectAll = () => {
    if (allSelectableSelected) {
      setSelectedSessionIds([]);
    } else {
      setSelectedSessionIds(selectableSessions.map((s) => s.sessionId));
    }
  };

  const runningTotal = outstandingSessions
    .filter((s) => selectedSessionIds.includes(s.sessionId))
    .reduce((sum, s) => sum + (s.amountDue ?? s.fee ?? 0), 0);

  const handleSendOutstandingRequest = async () => {
    try {
      setSendingOutstandingRequest(true);
      setOutstandingError(null);
      await api.post(`/api/payments/request-outstanding-payment/${clientId}`, {
        sessionIds: selectedSessionIds
      });
      setRequestModalOpen(false);
      setPaymentRequestSuccess('Payment request sent successfully');
      setTimeout(() => setPaymentRequestSuccess(null), 5000);
      await fetchPaymentSessionData();
    } catch (err: any) {
      if (err.response?.data?.code === 'DUPLICATE_REQUEST') {
        setActivePaymentRequest(err.response.data.existingRequest);
        setOutstandingError('An active payment request already exists for one or more selected sessions.');
      } else {
        setOutstandingError(err.response?.data?.error || 'Failed to send payment request');
      }
    } finally {
      setSendingOutstandingRequest(false);
    }
  };

  const handleResendOutstandingRequest = async () => {
    if (!activePaymentRequest) return;
    try {
      setResendingRequest(true);
      setOutstandingError(null);
      await api.post(`/api/payments/resend-outstanding-payment/${activePaymentRequest._id}`);
      setPaymentRequestSuccess('Payment link resent successfully');
      setTimeout(() => setPaymentRequestSuccess(null), 5000);
      await fetchOutstandingSessions();
    } catch (err: any) {
      setOutstandingError(err.response?.data?.error || 'Failed to resend payment request');
    } finally {
      setResendingRequest(false);
    }
  };

  const renderPaymentSummary = () => {
    if (!paymentSummary) return null;

    return (
      <Box sx={{ mb: 3 }}>
        <div className="glass-card p-6">
          <Typography variant="h6" className="theme-text-primary" gutterBottom>
            Payment Summary
          </Typography>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Payment Type
              </Typography>
              <Chip 
                label={formatPaymentType(paymentSummary.paymentType)} 
                sx={{ 
                  backgroundColor: 'rgba(79, 70, 229, 0.2)',
                  color: 'rgba(255, 255, 255, 0.95)',
                  borderColor: 'rgba(79, 70, 229, 0.5)'
                }}
                variant="outlined" 
              />
            </Box>
            {paymentSummary.sessionRate && (
              <Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Session Rate
                </Typography>
                <Typography variant="h6" className="theme-text-primary">
                  {formatPounds(paymentSummary.sessionRate)}
                </Typography>
              </Box>
            )}
            {(paymentSummary.assessmentFee != null || paymentSummary.assessmentFeeWaived) && (
              <Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Assessment Fee
                </Typography>
                <Typography variant="h6" sx={{ color: (paymentSummary.assessmentFeePaid || paymentSummary.assessmentFeeWaived) ? '#48bb78' : '#f56565' }}>
                  {paymentSummary.assessmentFeeWaived
                    ? 'Waived'
                    : `${formatPounds(paymentSummary.assessmentFee!)}${paymentSummary.assessmentFeePaid ? ' ✓' : ''}`}
                </Typography>
                {isAdmin && !paymentSummary.assessmentFeePaid && (
                  <Button
                    size="small"
                    onClick={handleToggleFeeWaiver}
                    sx={{ textTransform: 'none', fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', p: 0, minWidth: 0 }}
                  >
                    {paymentSummary.assessmentFeeWaived ? 'Reinstate fee' : 'Waive fee'}
                  </Button>
                )}
              </Box>
            )}
            <Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Total Paid
              </Typography>
              <Typography variant="h6" sx={{ color: '#48bb78' }}>
                {formatPounds(paymentSummary.totalPaid)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {paymentSummary.totalOutstanding < 0 ? 'Credit' : 'Outstanding'}
              </Typography>
              <Typography variant="h6" sx={{ color: paymentSummary.totalOutstanding > 0 ? "#f56565" : "#48bb78" }}>
                {paymentSummary.totalOutstanding < 0 
                  ? formatCurrency(Math.abs(paymentSummary.totalOutstanding))
                  : formatCurrency(paymentSummary.totalOutstanding)
                }
              </Typography>
            </Box>
            {paymentSummary.employerReference && (
              <Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Employer Reference
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                  {paymentSummary.employerReference}
                </Typography>
              </Box>
            )}
            {paymentSummary.activeBlockBooking && (
              <Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Active Block Booking
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                  {paymentSummary.activeBlockBooking.sessionsUsed} / {paymentSummary.activeBlockBooking.numberOfSessions} sessions used
                </Typography>
              </Box>
            )}
          </Box>
        </div>
      </Box>
    );
  };

  const renderPaymentsTab = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" className="theme-text-primary">Payment History</Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {!isCounsellor && sessions.some((s) => s.paymentStatus !== 'paid') && (
            <Button
              variant="contained"
              onClick={handleOpenRequestModal}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                minWidth: '160px',
                background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)'
                }
              }}
            >
              Request Outstanding Payment
            </Button>
          )}
          <Button 
            variant="contained" 
            onClick={() => setCreatePaymentOpen(true)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              minWidth: '160px',
              backgroundColor: '#4caf50',
              '&:hover': {
                backgroundColor: '#388e3c'
              }
            }}
          >
            Record Payment
          </Button>
          {isAdmin && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setWriteOffOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderColor: '#f56565',
                color: '#f56565',
                '&:hover': { borderColor: '#e53e3e', color: '#e53e3e', backgroundColor: 'rgba(245,101,101,0.08)' }
              }}
            >
              Write Off Debt
            </Button>
          )}
        </Box>
      </Box>
      <div className="glass-card rounded-lg overflow-hidden">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 700 }}>Payment Date</TableCell>
              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 700 }}>Session Date</TableCell>
              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 700 }}>Amount</TableCell>
              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((payment) => (
              <TableRow key={payment._id}>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                  {formatSafeDate(payment.createdAt)}
                </TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                  {payment.session?.date ? formatSafeDate(payment.session.date, 'dd/MM/yyyy') : '-'}
                </TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                  {formatPounds(payment.amount)}
                </TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                  <Chip 
                    label={formatPaymentType(payment.type)} 
                    size="small" 
                    variant="outlined" 
                  />
                </TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                  <Chip 
                    label={formatStatus(payment.status)} 
                    color={getPaymentStatusColor(payment.status) as any}
                    size="small" 
                  />
                </TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                  {payment.status === 'succeeded' && (
                    <Button 
                      size="small" 
                      variant="outlined"
                      onClick={() => setReceiptPayment(payment)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '80px',
                        borderColor: '#4caf50',
                        color: '#4caf50',
                        '&:hover': {
                          borderColor: '#388e3c',
                          color: '#388e3c',
                          backgroundColor: 'rgba(76, 175, 80, 0.04)'
                        }
                      }}
                    >
                      Receipt
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Box>
  );

  const renderSessionsTab = () => {
    const completedSessions = sessions.filter(s => s.status === 'completed').length;
    const previousSessions = client?.previousSessions || 0;
    const totalCompletedSessions = completedSessions + previousSessions;
    const scheduledSessions = sessions.filter(s => s.status === 'scheduled');
    const nextSession = scheduledSessions.length > 0 ? scheduledSessions[0] : null;
    // Use numberOfSessions from client (organization allocation), not block booking
    const totalAllowed = client?.numberOfSessions;

    return (
      <Box>
        {/* Previous Sessions Banner */}
        {previousSessions > 0 && (
          <Box sx={{ mb: 3 }}>
            <div style={{ 
              backgroundColor: 'rgba(245, 158, 11, 0.1)', 
              borderLeft: '4px solid #f59e0b', 
              padding: '16px', 
              borderRadius: '8px'
            }}>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 600, mb: 0.5 }}>
                Previous Sessions
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                {previousSessions} session{previousSessions !== 1 ? 's' : ''} ({previousSessions} hour{previousSessions !== 1 ? 's' : ''}) completed before moving to digital system
              </Typography>
            </div>
          </Box>
        )}

        {/* Session Summary */}
        <Box sx={{ mb: 3 }}>
          <div className="glass-card rounded-lg p-6">
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                  Total Sessions Completed
                </Typography>
                <Typography variant="h4" sx={{ color: '#48bb78', fontWeight: 700 }}>
                  {totalCompletedSessions}
                </Typography>
                {previousSessions > 0 && (
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mt: 0.5 }}>
                    ({completedSessions} digital + {previousSessions} previous)
                  </Typography>
                )}
              </Box>
              {totalAllowed ? (
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                    Sessions Allowed
                  </Typography>
                  <Typography variant="h3" sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 700, fontSize: '2.5rem' }}>
                    {totalCompletedSessions} / {totalAllowed}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 0.5 }}>
                    {totalAllowed - totalCompletedSessions} remaining
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                    Sessions Allowed
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#4ade80', fontWeight: 700 }}>
                    Unlimited
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 0.5 }}>
                    Self-funded client
                  </Typography>
                </Box>
              )}
              {nextSession && (
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}>
                    Next Session
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 600 }}>
                    {formatSafeDate(nextSession.date)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    with {nextSession.counsellor ? `${nextSession.counsellor.firstName} ${nextSession.counsellor.lastName}` : 'N/A'}
                  </Typography>
                </Box>
              )}
            </Box>
          </div>
        </Box>

        {/* Session History Table - Desktop */}
        <Typography variant="h6" className="theme-text-primary" sx={{ mb: 2 }}>Session History</Typography>
        
        {/* Mobile Card View */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {sessions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, color: 'rgba(255, 255, 255, 0.5)' }}>
              <Typography variant="body1">No sessions recorded yet</Typography>
            </Box>
          ) : (
            sessions.map((session) => (
              <Card
                key={session._id}
                sx={{
                  mb: 2,
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 0.5 }}>
                        Date
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                        {formatSafeDate(session.date, 'dd MMM yyyy HH:mm')}
                      </Typography>
                    </Box>
                    <Chip 
                      label={formatStatus(session.status)} 
                      color={getSessionStatusColor(session.status) as any}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                  
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 0.5 }}>
                      Counsellor
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      {session.counsellor ? `${session.counsellor.firstName} ${session.counsellor.lastName}` : 'N/A'}
                    </Typography>
                  </Box>

                  {session.notes && (
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 0.5 }}>
                        Notes
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                        {session.notes}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </Box>

        {/* Desktop Table View */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <div className="glass-card rounded-lg overflow-hidden overflow-x-auto">
          <Table sx={{ minWidth: { xs: 300, sm: 650 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }}>Date</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }}>Counsellor</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }}>Status</TableCell>
                <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      No sessions recorded yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sessions.map((session) => (
                  <TableRow key={session._id}>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
                      {formatSafeDate(session.date)}
                    </TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
                      {session.counsellor ? `${session.counsellor.firstName} ${session.counsellor.lastName}` : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 } }}>
                      <Chip 
                        label={formatStatus(session.status)} 
                        color={getSessionStatusColor(session.status) as any}
                        size="small"
                        sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1.5, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>
                      {session.notes && (
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {session.notes}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        </Box>
      </Box>
    );
  };

  const renderBlockBookingsTab = () => (
    <Box>
      <Typography variant="h6" className="theme-text-primary" sx={{ mb: 2 }}>Block Bookings</Typography>
      <div className="glass-card rounded-lg overflow-hidden">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 700 }}>Start Date</TableCell>
              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 700 }}>Sessions</TableCell>
              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 700 }}>Total Fee</TableCell>
              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 700 }}>Sessions Used</TableCell>
              <TableCell sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 700 }}>Payment Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {blockBookings.map((booking) => (
              <TableRow key={booking._id}>
                <TableCell>
                  {formatSafeDate(booking.startDate, 'dd/MM/yyyy')}
                </TableCell>
                <TableCell>{booking.numberOfSessions}</TableCell>
                <TableCell>{formatPounds(booking.fee)}</TableCell>
                <TableCell>
                  <Chip 
                    label={booking.status} 
                    color={booking.status === 'active' ? 'success' : 'default'}
                    size="small" 
                  />
                </TableCell>
                <TableCell>
                  {booking.sessionsUsed} / {booking.numberOfSessions}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={booking.isPaid ? 'Paid' : 'Pending'} 
                    color={booking.isPaid ? 'success' : 'warning'}
                    size="small" 
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Box>
  );

  if (loading) {
    return <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>Loading payment and session data...</Typography>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <>
      <div className="space-y-6">
        <div className="glass-card-light rounded-lg p-6">
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom
            sx={{
              fontFamily: 'Roboto',
              fontWeight: 700,
              fontSize: '1.8rem',
              color: 'rgba(255, 255, 255, 0.95)',
              letterSpacing: '0.5px',
              marginBottom: 3
            }}
          >
            Sessions
          </Typography>

          {paymentRecordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPaymentRecordSuccess(null)}>
              {paymentRecordSuccess}
            </Alert>
          )}
          {paymentRequestSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPaymentRequestSuccess(null)}>
              {paymentRequestSuccess}
            </Alert>
          )}
          
          {!isCounsellor && renderPaymentSummary()}
          
          {!isCounsellor ? (
            <>
              <Box sx={{ borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.2)', mt: 3 }}>
                <Tabs 
                  value={activeTab} 
                  onChange={(_, newValue) => setActiveTab(newValue)}
                  sx={{
                    '& .MuiTab-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontWeight: 600,
                      '&.Mui-selected': {
                        color: 'rgba(255, 255, 255, 0.95)'
                      }
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: 'rgba(255, 255, 255, 0.95)'
                    }
                  }}
                >
                  <Tab label="Payments" />
                  <Tab label="Sessions" />
                  <Tab label="Block Bookings" />
                </Tabs>
              </Box>

              <Box sx={{ mt: 3 }}>
                {activeTab === 0 && renderPaymentsTab()}
                {activeTab === 1 && renderSessionsTab()}
                {activeTab === 2 && renderBlockBookingsTab()}
              </Box>
            </>
          ) : (
            <Box sx={{ mt: 3 }}>
              {renderSessionsTab()}
            </Box>
          )}
        </div>
      </div>

      {/* Create Payment Dialog */}
      <Dialog 
        open={createPaymentOpen} 
        onClose={() => setCreatePaymentOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
          color: 'white',
          fontWeight: 700
        }}>
          Record Payment
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Amount"
              type="number"
              value={newPayment.amount}
              onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
              fullWidth
              sx={{
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)'
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'rgba(255, 255, 255, 0.9)'
                },
                '& .MuiOutlinedInput-input': {
                  color: 'white'
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.7)'
                  }
                }
              }}
            />
            <FormControl fullWidth
              sx={{
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)'
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'rgba(255, 255, 255, 0.9)'
                },
                '& .MuiSelect-select': {
                  color: 'white'
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.7)'
                  }
                },
                '& .MuiSvgIcon-root': {
                  color: 'rgba(255, 255, 255, 0.7)'
                }
              }}
            >
              <InputLabel>Payment Type</InputLabel>
              <Select
                value={newPayment.type}
                onChange={(e) => setNewPayment({ ...newPayment, type: e.target.value as any })}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: 'rgba(30, 41, 59, 0.97)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      '& .MuiMenuItem-root': { color: 'white' },
                      '& .MuiMenuItem-root:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
                      '& .MuiMenuItem-root.Mui-selected': { backgroundColor: 'rgba(99, 102, 241, 0.3)' },
                      '& .MuiMenuItem-root.Mui-selected:hover': { backgroundColor: 'rgba(99, 102, 241, 0.45)' }
                    }
                  }
                }}
              >
                <MenuItem value="single_session">Single Session</MenuItem>
                <MenuItem value="block_booking">Block Booking</MenuItem>
                <MenuItem value="manual">Outstanding balance (auto-allocate)</MenuItem>
              </Select>
            </FormControl>
            {newPayment.type === 'manual' && (
              <>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  The amount received is allocated to the oldest outstanding sessions first.
                  Sessions it fully covers are marked paid; any remainder is held as account credit.
                </Typography>
                <FormControl fullWidth
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: 'rgba(255, 255, 255, 0.9)'
                    },
                    '& .MuiSelect-select': {
                      color: 'white'
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.7)'
                      }
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                >
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={newPayment.method}
                    onChange={(e) => setNewPayment({ ...newPayment, method: e.target.value })}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: 'rgba(30, 41, 59, 0.97)',
                          backdropFilter: 'blur(20px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          '& .MuiMenuItem-root': { color: 'white' },
                          '& .MuiMenuItem-root:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
                          '& .MuiMenuItem-root.Mui-selected': { backgroundColor: 'rgba(99, 102, 241, 0.3)' },
                          '& .MuiMenuItem-root.Mui-selected:hover': { backgroundColor: 'rgba(99, 102, 241, 0.45)' }
                        }
                      }
                    }}
                  >
                    <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="cheque">Cheque</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Reference (optional)"
                  value={newPayment.reference}
                  onChange={(e) => setNewPayment({ ...newPayment, reference: e.target.value })}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: 'rgba(255, 255, 255, 0.9)'
                    },
                    '& .MuiOutlinedInput-input': {
                      color: 'white'
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.7)'
                      }
                    }
                  }}
                />
              </>
            )}
            {newPayment.type === 'single_session' && (
              <FormControl fullWidth
                sx={{
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  },
                  '& .MuiSelect-select': {
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)'
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.7)'
                    }
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              >
                <InputLabel>Session</InputLabel>
                <Select
                  value={newPayment.sessionId}
                  onChange={(e) => setNewPayment({ ...newPayment, sessionId: e.target.value })}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: 'rgba(30, 41, 59, 0.97)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        '& .MuiMenuItem-root': { color: 'white' },
                        '& .MuiMenuItem-root:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
                        '& .MuiMenuItem-root.Mui-selected': { backgroundColor: 'rgba(99, 102, 241, 0.3)' },
                        '& .MuiMenuItem-root.Mui-selected:hover': { backgroundColor: 'rgba(99, 102, 241, 0.45)' }
                      }
                    }
                  }}
                >
                  {sessions.filter(s => s.paymentStatus !== 'paid').map((session) => (
                    <MenuItem key={session._id} value={session._id}>
                      {formatSafeDate(session.date)} - {session.fee ? formatPounds(session.fee) : '£0.00'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setCreatePaymentOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreatePayment} 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)'
              }
            }}
          >
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Receipt Dialog */}
      <Dialog
        open={!!receiptPayment}
        onClose={() => setReceiptPayment(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(30, 41, 59, 0.97)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
          color: 'white',
          fontWeight: 700
        }}>
          Payment Receipt
        </DialogTitle>
        <DialogContent>
          {receiptPayment && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Date</Typography>
                  <Typography className="theme-text-primary">
                    {formatSafeDate(receiptPayment.createdAt)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Receipt Ref</Typography>
                  <Typography className="theme-text-primary" sx={{ fontFamily: 'monospace' }}>
                    {receiptPayment._id.slice(-8).toUpperCase()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Amount</Typography>
                  <Typography className="theme-text-primary" sx={{ fontWeight: 700 }}>
                    {formatPounds(receiptPayment.amount)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Type</Typography>
                  <Typography className="theme-text-primary">
                    {formatPaymentType(receiptPayment.type)}
                  </Typography>
                </Box>
                {receiptPayment.metadata?.method && (
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Method</Typography>
                    <Typography className="theme-text-primary">
                      {formatPaymentType(receiptPayment.metadata.method)}
                    </Typography>
                  </Box>
                )}
                {receiptPayment.metadata?.reference && (
                  <Box>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Reference</Typography>
                    <Typography className="theme-text-primary">
                      {receiptPayment.metadata.reference}
                    </Typography>
                  </Box>
                )}
              </Box>

              {(receiptPayment.metadata?.assessmentFeePence ||
                receiptPayment.metadata?.sessionsAllocatedPence ||
                receiptPayment.metadata?.balanceAppliedPence ||
                receiptPayment.metadata?.creditPence) ? (
                <Box sx={{
                  p: 2, borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: 'rgba(255,255,255,0.04)'
                }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1, fontWeight: 700 }}>
                    Allocation
                  </Typography>
                  {!!receiptPayment.metadata?.assessmentFeePence && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" className="theme-text-primary">Assessment fee</Typography>
                      <Typography variant="body2" className="theme-text-primary">
                        {formatPounds((receiptPayment.metadata.assessmentFeePence || 0) / 100)}
                      </Typography>
                    </Box>
                  )}
                  {!!receiptPayment.metadata?.sessionsAllocatedPence && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" className="theme-text-primary">
                        Sessions{receiptPayment.sessions?.length ? ` (${receiptPayment.sessions.length})` : ''}
                      </Typography>
                      <Typography variant="body2" className="theme-text-primary">
                        {formatPounds((receiptPayment.metadata.sessionsAllocatedPence || 0) / 100)}
                      </Typography>
                    </Box>
                  )}
                  {receiptPayment.sessions?.map((sid) => {
                    const s = sessions.find(x => x._id === sid);
                    return s ? (
                      <Box key={sid} sx={{ display: 'flex', justifyContent: 'space-between', pl: 2 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                          {formatSafeDate(s.date)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                          {formatPounds(s.fee)}
                        </Typography>
                      </Box>
                    ) : null;
                  })}
                  {!!receiptPayment.metadata?.balanceAppliedPence && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" className="theme-text-primary">Account balance</Typography>
                      <Typography variant="body2" className="theme-text-primary">
                        {formatPounds((receiptPayment.metadata.balanceAppliedPence || 0) / 100)}
                      </Typography>
                    </Box>
                  )}
                  {!!receiptPayment.metadata?.creditPence && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ color: '#48bb78' }}>Credit held</Typography>
                      <Typography variant="body2" sx={{ color: '#48bb78' }}>
                        {formatPounds((receiptPayment.metadata.creditPence || 0) / 100)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : receiptPayment.session ? (
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>Session</Typography>
                  <Typography className="theme-text-primary">
                    {formatSafeDate(receiptPayment.session.date)}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
          {receiptEmailState.sent && (
            <Alert severity="success" onClose={() => setReceiptEmailState({ ...receiptEmailState, sent: null })}>
              {receiptEmailState.sent}
            </Alert>
          )}
          {receiptEmailState.error && (
            <Alert severity="error" onClose={() => setReceiptEmailState({ ...receiptEmailState, error: null })}>
              {receiptEmailState.error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              onClick={() => { setReceiptPayment(null); setReceiptEmailState({ sending: false, sent: null, error: null }); }}
              variant="outlined"
              sx={{
                textTransform: 'none',
                borderColor: 'rgba(255,255,255,0.3)',
                color: 'rgba(255,255,255,0.8)',
                '&:hover': { borderColor: 'rgba(255,255,255,0.5)' }
              }}
            >
              Close
            </Button>
            <Button
              onClick={handleEmailReceipt}
              disabled={receiptEmailState.sending}
              variant="contained"
              sx={{
                textTransform: 'none',
                background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)' },
                '&.Mui-disabled': { opacity: 0.6 }
              }}
            >
              {receiptEmailState.sending ? 'Sending…' : 'Email Receipt to Client'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Write Off Debt Dialog */}
      <Dialog
        open={writeOffOpen}
        onClose={() => setWriteOffOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(30, 41, 59, 0.97)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }
        }}
      >
        <DialogTitle sx={{ color: 'white', fontWeight: 700 }}>Write Off Debt?</DialogTitle>
        <DialogContent>
          <Typography className="theme-text-primary" sx={{ mb: 2 }}>
            This zeroes the client's outstanding balance and marks all unpaid
            sessions as waived. The debt is forgiven - it is not recorded as
            paid. This cannot be undone automatically.
          </Typography>
          <TextField
            label="Reason (optional)"
            value={writeOffReason}
            onChange={(e) => setWriteOffReason(e.target.value)}
            fullWidth
            sx={{
              '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
              '& .MuiOutlinedInput-input': { color: 'white' },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' }
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setWriteOffOpen(false)} variant="outlined"
            sx={{ textTransform: 'none', borderColor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.8)' }}>
            Cancel
          </Button>
          <Button onClick={handleWriteOff} variant="contained"
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(135deg, #e53e3e 0%, #c53030 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #c53030 0%, #9b2c2c 100%)' }
            }}>
            Write Off Debt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request Outstanding Payment Dialog */}
      <Dialog
        open={requestModalOpen}
        onClose={() => setRequestModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
          color: 'white',
          fontWeight: 700
        }}>
          Request Outstanding Payment
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {outstandingError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setOutstandingError(null)}>
              {outstandingError}
            </Alert>
          )}

          {activePaymentRequest && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleResendOutstandingRequest}
                  disabled={resendingRequest}
                >
                  {resendingRequest ? 'Resending...' : 'Resend Payment Link'}
                </Button>
              }
            >
              A payment request for {formatPounds(activePaymentRequest.amount)} covering{' '}
              {activePaymentRequest.sessionIds.length} session(s) is already active
              (sent {formatSafeDate(activePaymentRequest.createdAt, 'dd/MM/yyyy')}).
            </Alert>
          )}

          {loadingOutstanding ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : outstandingSessions.length === 0 ? (
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', py: 2 }}>
              No outstanding sessions found.
            </Typography>
          ) : (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelectableSelected}
                    indeterminate={!allSelectableSelected && selectedSessionIds.length > 0}
                    onChange={handleToggleSelectAll}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      '&.Mui-checked': { color: '#818cf8' },
                      '&.MuiCheckbox-indeterminate': { color: '#818cf8' }
                    }}
                  />
                }
                label="Select All"
                sx={{ color: 'rgba(255, 255, 255, 0.95)', mb: 1, fontWeight: 600 }}
              />
              <Box
                sx={{
                  maxHeight: 320,
                  overflowY: 'auto',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  borderRadius: 2
                }}
              >
                {outstandingSessions.map((session, index) => (
                  <Box
                    key={session.sessionId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 1.5,
                      py: 0.5,
                      opacity: session.pendingRequestId ? 0.5 : 1,
                      borderBottom: index < outstandingSessions.length - 1 ? '1px solid rgba(255, 255, 255, 0.08)' : 'none'
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedSessionIds.includes(session.sessionId)}
                          onChange={() => toggleSessionSelected(session.sessionId)}
                          disabled={!!session.pendingRequestId}
                          sx={{
                            color: 'rgba(255, 255, 255, 0.5)',
                            '&.Mui-checked': { color: '#818cf8' }
                          }}
                        />
                      }
                      label={formatSafeDate(session.date, 'dd/MM/yyyy')}
                      sx={{ color: 'rgba(255, 255, 255, 0.95)' }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {session.pendingRequestId && (
                        <Chip label="Request Pending" size="small" color="info" />
                      )}
                      <Typography sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                        {formatPounds(session.amountDue ?? session.fee)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: 2,
                  pt: 2,
                  borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
                  Total to Request
                </Typography>
                <Typography variant="h6" sx={{ color: '#48bb78', fontWeight: 700 }}>
                  {formatPounds(runningTotal)}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setRequestModalOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendOutstandingRequest}
            variant="contained"
            disabled={selectedSessionIds.length === 0 || sendingOutstandingRequest}
            sx={{
              background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)'
              }
            }}
          >
            {sendingOutstandingRequest ? 'Sending...' : 'Send Payment Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PaymentSessionHistory;
